/**
 * Cliente (widget) da rota /api/campaign-form: uma requisição por contato, com
 * cache em memória e retry. Usado pelo modal de informações da chamada (mobile e
 * inline), pelo cartão da tela de chamada do mobile, pelo painel do discador
 * flutuante e pelo detalhe do histórico.
 */
import type { CampaignCallInfo } from '@callixbrasil/client-sdk';
import { API_BASE_URL, getAuthHeaders } from './widgetApi';
import type { CampaignFormResponse, CampaignFormSource } from './campaignFormTypes';

export interface CampaignFormRequest {
  source: CampaignFormSource;
  id: number;
  campaignId?: number | null;
  formId?: number | null;
  phoneId?: number | null;
  /** Prova emitida por /api/call-history (histórico) de que o contato é de uma chamada do agente. */
  signature?: string | null;
  expiresAt?: number | null;
}

export interface FetchCampaignFormOptions {
  apiBaseUrl?: string;
  headers?: Record<string, string>;
  /** Ignora o cache. */
  force?: boolean;
  /** Tentativas extras em falha de rede/5xx (padrão 2). */
  retries?: number;
}

export const CAMPAIGN_FORM_CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_RETRIES = 2;
const RETRY_DELAY_MS = 800;
const REQUEST_TIMEOUT_MS = 15000;

const cache = new Map<string, { promise: Promise<CampaignFormResponse>; until: number }>();

export function campaignFormCacheKey(request: CampaignFormRequest): string {
  return `${request.source}:${request.id}`;
}

/** Monta o pedido a partir do CampaignCallInfo do SDK (campanha comum ou campanha de clientes). */
export function campaignFormRequestFromCallInfo(
  info: CampaignCallInfo | null | undefined,
  phoneId?: number | null
): CampaignFormRequest | null {
  const contact = info?.campaignContact;
  if (!contact?.id) {
    return null;
  }
  const customer = (contact as { customer?: { id?: number; formId?: number } }).customer;
  if (customer?.id && customer?.formId) {
    return { source: 'customer', id: customer.id, formId: customer.formId, campaignId: info?.campaign?.id ?? null, phoneId: phoneId ?? null };
  }
  return { source: 'campaign', id: contact.id, campaignId: info?.campaign?.id ?? null, phoneId: phoneId ?? null };
}

export function buildCampaignFormUrl(apiBaseUrl: string, request: CampaignFormRequest): string {
  const params = new URLSearchParams();
  if (request.source === 'customer') params.set('source', 'customer');
  if (request.campaignId) params.set('campaignId', String(request.campaignId));
  if (request.formId) params.set('formId', String(request.formId));
  if (request.phoneId) params.set('phoneId', String(request.phoneId));
  if (request.signature && request.expiresAt) {
    params.set('sig', request.signature);
    params.set('exp', String(request.expiresAt));
  }
  const query = params.toString();
  return `${apiBaseUrl}/api/campaign-form/${encodeURIComponent(String(request.id))}${query ? `?${query}` : ''}`;
}

function isFormResponse(value: unknown): value is CampaignFormResponse {
  if (!value || typeof value !== 'object') return false;
  const form = value as Partial<CampaignFormResponse>;
  return typeof form.id === 'number' && !!form.values && typeof form.values === 'object' && Array.isArray(form.fields);
}

type RequestError = Error & { status?: number; cause?: unknown };

export const NETWORK_ERROR_MESSAGE = 'Não foi possível conectar ao servidor do discador. Verifique a conexão ou se o back-end está atualizado.';
export const ROUTE_MISSING_MESSAGE = 'Rota do formulário não encontrada no servidor do discador. Atualize o back-end (deploy) para esta versão do widget.';

/** Mensagem para o operador a partir do status HTTP e do corpo (JSON) devolvido pelo back-end. */
function describeHttpError(status: number, body: { error?: string; message?: string }): string {
  if (status === 401) return 'Sessão expirada. Entre novamente no discador.';
  const fromBody = body.message || body.error;
  if (fromBody) return fromBody;
  // 404 sem JSON é a página de erro do Next: a rota não existe nesse deploy do back-end
  if (status === 404) return ROUTE_MISSING_MESSAGE;
  return `Erro ${status}`;
}

async function requestOnce(url: string, headers: Record<string, string>): Promise<CampaignFormResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { method: 'GET', headers, credentials: 'omit', signal: controller.signal });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
      const error = new Error(describeHttpError(response.status, body)) as RequestError;
      error.status = response.status;
      throw error;
    }
    const data: unknown = await response.json();
    if (!isFormResponse(data)) {
      throw new Error('Resposta inesperada do servidor');
    }
    return data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Mensagem em português (e sem status: continua passível de nova tentativa)
      throw new Error('Tempo esgotado ao buscar o formulário');
    }
    if (error instanceof TypeError) {
      // fetch rejeita com TypeError quando não há resposta: rede fora, CORS bloqueado
      // (ex.: rota inexistente no deploy, cujo preflight volta sem cabeçalhos) ou servidor fora do ar
      const wrapped = new Error(NETWORK_ERROR_MESSAGE) as RequestError;
      wrapped.cause = error;
      throw wrapped;
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

function shouldRetry(error: unknown): boolean {
  const status = (error as { status?: number })?.status;
  if (status === undefined) return true; // rede/timeout
  return status >= 500 || status === 408 || status === 429;
}

/** Busca o formulário (com cache por contato e retry em falhas de rede/5xx). */
export function fetchCampaignForm(request: CampaignFormRequest, options: FetchCampaignFormOptions = {}): Promise<CampaignFormResponse> {
  const key = campaignFormCacheKey(request);
  const now = Date.now();
  const cached = cache.get(key);
  if (cached && cached.until > now && !options.force) {
    return cached.promise;
  }
  const url = buildCampaignFormUrl(options.apiBaseUrl ?? API_BASE_URL, request);
  const headers = options.headers ?? getAuthHeaders();
  const retries = Math.max(0, options.retries ?? DEFAULT_RETRIES);
  const promise = (async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await requestOnce(url, headers);
      } catch (error) {
        lastError = error;
        if (attempt === retries || !shouldRetry(error)) break;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * 2 ** attempt));
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Erro ao buscar o formulário');
  })();
  // Quem chegar durante a busca compartilha a mesma promise
  cache.set(key, { promise, until: now + CAMPAIGN_FORM_CACHE_TTL_MS });
  promise
    .then((form) => {
      // Resposta parcial (rótulos técnicos) não fica no cache: a próxima busca tende a vir completa
      if (form.partial && cache.get(key)?.promise === promise) cache.delete(key);
    })
    .catch(() => {
      if (cache.get(key)?.promise === promise) cache.delete(key);
    });
  return promise;
}

/** Dispara a busca sem esperar (ex.: assim que a chamada de campanha toca). */
export function prefetchCampaignForm(request: CampaignFormRequest, options: FetchCampaignFormOptions = {}): void {
  fetchCampaignForm(request, options).catch(() => undefined);
}

export function clearCampaignFormCache(): void {
  cache.clear();
}
